import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { SideTabsLayout } from '../../components/SideTabs';
import { useSearchParamsState } from '../../hooks';
import { FundOrder } from '../../types/portfolioOrder';
import { AttachmentsTab } from './AttachmentsTab';
import { ConsolidateRequestsTab } from './ConsolidateRequestsTab';
import { FundOrdersTab } from './FundOrdersTab';
import { PortfolioOrdersWeightsTab } from './PortfolioOrdersWeightsTab';
import { PortfolioOrderTab } from './PortfolioOrderTab';
import { RequestsTab } from './RequestsTab';
import { SatelliteRequestsTab } from './SatelliteRequestsTab';
import { AlpacaTab } from './AlpacaTab';
import { JadwaTab } from './JadwaTab';
import { DerayahTab } from './DerayahTab';
import ReconciliationRuns, { RECONCILIATION_ITEM_ID_PARAM, RECONCILIATION_RUN_ID_PARAM } from './ReconciliationRuns';
import {
  ALBILAD_TAB_VALUE,
  ALPACA_TAB_VALUE,
  DEFAULT_TAB_VALUE,
  DERAYAH_TAB_VALUE,
  getPortfolioOrderTabSections,
  JADWA_TAB_VALUE,
  mapTabParamToValue,
  mapTabValueToTabParam,
  SNB_TAB_VALUE,
} from './portfolioOrderTabs';
import { ALBILAD_FUND_MANAGER, SNB_FUND_MANAGER } from './components/jadwa/helpers';

interface PortfolioOrderTabListProps {
  orderId?: string;
  fundOrdersKey: string;
  onSelectFundOrders: (ids: FundOrder[]) => void;
  showAlpacaTab: boolean;
  showJadwaTab: boolean;
  showAlbiladTab: boolean;
  showDerayahTab: boolean;
  showSnbTab: boolean;
}

export function PortfolioOrderTabList(props: PortfolioOrderTabListProps) {
  const {
    orderId,
    fundOrdersKey,
    onSelectFundOrders,
    showAlpacaTab,
    showJadwaTab,
    showAlbiladTab,
    showDerayahTab,
    showSnbTab,
  } = props;

  const [selectedTab, setSelectedTab] = useSearchParamsState('tab', 'default');
  const [, setSearchParams] = useSearchParams();

  const activeTabValue = mapTabParamToValue[selectedTab] || DEFAULT_TAB_VALUE;
  const sections = React.useMemo(
    () => getPortfolioOrderTabSections({ showAlpacaTab, showJadwaTab, showAlbiladTab, showDerayahTab, showSnbTab }),
    [showAlpacaTab, showJadwaTab, showAlbiladTab, showDerayahTab, showSnbTab],
  );

  const handleChange = (newValue: string) => {
    const nextTab = mapTabValueToTabParam[newValue];
    if (nextTab === 'reconciliationRuns') {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', 'reconciliationRuns');
          next.delete(RECONCILIATION_RUN_ID_PARAM);
          next.delete(RECONCILIATION_ITEM_ID_PARAM);
          return next;
        },
        { replace: true },
      );
    } else {
      setSelectedTab(nextTab);
    }
  };

  if (!orderId) {
    return <div>Loading...</div>;
  }

  const renderActivePanel = () => {
    switch (activeTabValue) {
      case '1':
        return <PortfolioOrderTab orderId={orderId} />;
      case '2':
        return <FundOrdersTab key={fundOrdersKey} onSelectFundOrders={onSelectFundOrders} orderId={orderId} />;
      case '3':
        return <PortfolioOrdersWeightsTab orderId={orderId} />;
      case '4':
        return <RequestsTab orderId={orderId} />;
      case '5':
        return <SatelliteRequestsTab orderId={orderId} />;
      case '6':
        return <ConsolidateRequestsTab orderId={orderId} />;
      case '7':
        return <AttachmentsTab orderId={orderId} />;
      case ALPACA_TAB_VALUE:
        return showAlpacaTab ? <AlpacaTab orderId={orderId} /> : null;
      case JADWA_TAB_VALUE:
        return showJadwaTab ? <JadwaTab orderId={orderId} /> : null;
      case ALBILAD_TAB_VALUE:
        return showAlbiladTab ? <JadwaTab orderId={orderId} fundManager={ALBILAD_FUND_MANAGER} /> : null;
      case DERAYAH_TAB_VALUE:
        return showDerayahTab ? <DerayahTab orderId={orderId} /> : null;
      case SNB_TAB_VALUE:
        return showSnbTab ? <JadwaTab orderId={orderId} fundManager={SNB_FUND_MANAGER} /> : null;
      case '11':
        return <ReconciliationRuns orderId={orderId} />;
      default:
        return null;
    }
  };

  return (
    <SideTabsLayout
      sections={sections}
      ariaLabel="Portfolio order sections"
      value={activeTabValue}
      onChange={handleChange}
    >
      {renderActivePanel()}
    </SideTabsLayout>
  );
}
